/**
 * MemberDetailDrawer — Cockpit Detail-Sicht für ein Org-Mitglied.
 *
 * Tabs:
 *   - Identität     : Name, Email, Status, Account-Type, Onboarding-Daten
 *   - Org-Rolle     : owner/admin/member (Inhaber-Schutz)
 *   - Berechtigungen: Realm-Group-Toggle-Liste (Konvention `/services/<name>`
 *                     für neue Groups; bestehende Legacy-Groups bleiben
 *                     sichtbar bis zur Phase-2-Migration)
 *   - Audit         : Stub — Cortex-Audit-Log Integration kommt mit
 *                     Aria-im-Cockpit
 *
 * Daten kommen ueber api.getMemberDetail (Live-Read aus Keycloak +
 * organization_members DB) und api.listOrgGroups (Realm-Groups flat).
 *
 * Schreib-Operationen (Group-Toggle, Rolle aendern) optimistic update +
 * Refetch bei Fehler.
 */

import { useEffect, useState, useMemo, useCallback } from 'react'
import { api } from '../services/api'
import type {
  CortexStandingPermission,
  CortexPermissionType,
  CortexPermissionScope,
} from '../services/api'
import { useToast } from '../components/ui'
import { isTenantInstance } from '../lib/runtimeConfig'
import { displayIdentity, isItsceServiceAccount } from '../utils/identityDisplay'

type AccountType = 'person' | 'service' | 'mailbox'

interface MemberDetail {
  keycloak_id: string
  username: string
  email: string
  first_name?: string
  last_name?: string
  enabled: boolean
  account_type: AccountType
  org_role: string
  org_status?: string
  invited_at?: string
  accepted_at?: string
  groups: { id: string; name: string; path: string }[]
}

interface RealmGroup {
  id: string
  name: string
  path: string
}

interface Props {
  orgSlug: string
  memberRef: string | number   // keycloak_id (UUID) oder DB-PK
  isAdminOrOwner: boolean
  onClose: () => void
  onChanged: () => void
}

const ACCOUNT_TYPE_LABEL: Record<AccountType, string> = {
  person: 'Person',
  service: 'Service',
  mailbox: 'Postfach',
}

type Tab = 'identity' | 'role' | 'permissions' | 'audit'

const PERMISSION_TYPE_LABEL: Record<CortexPermissionType, string> = {
  allow: 'Erlauben',
  confirm: 'Bestätigung erforderlich',
  deny: 'Verbieten',
}

const SCOPE_LABEL: Record<CortexPermissionScope, string> = {
  tool: 'Tool',
  server: 'Server',
  category: 'Kategorie',
}

const PERMISSION_TYPE_STYLE: Record<CortexPermissionType, { bg: string; fg: string; border: string }> = {
  allow: { bg: '#ecfdf5', fg: '#065f46', border: '#a7f3d0' },
  confirm: { bg: '#fffbeb', fg: '#92400e', border: '#fde68a' },
  deny: { bg: '#fef2f2', fg: '#991b1b', border: '#fecaca' },
}

/**
 * TQ #6984 (Confirm-Engine P4): Editor fuer die standing_permissions eines
 * Mitglieds. Liest/setzt/loescht ueber die Cortex Admin-Grant-Endpoints
 * (GET|POST|DELETE /api/v1/admin/users/{user_id}/permissions). userId ist
 * die Keycloak-/Cortex-User-ID (= MemberDetail.keycloak_id).
 */
function StandingPermissionsEditor({
  userId,
  canEdit,
}: {
  userId: string
  canEdit: boolean
}) {
  const { showToast } = useToast()
  const [grants, setGrants] = useState<CortexStandingPermission[]>([])
  const [loading, setLoading] = useState(true)
  const [unavailable, setUnavailable] = useState(false)
  const [busy, setBusy] = useState(false)
  const [removingId, setRemovingId] = useState<string | null>(null)

  // Formular-State
  const [permType, setPermType] = useState<CortexPermissionType>('confirm')
  const [scope, setScope] = useState<CortexPermissionScope>('tool')
  const [target, setTarget] = useState('')
  const [expiresAt, setExpiresAt] = useState('')

  const load = useCallback(async () => {
    if (!userId) return
    setLoading(true)
    try {
      const list = await api.listUserGrants(userId)
      setGrants(list)
      setUnavailable(false)
    } catch (e: unknown) {
      const err = e as { status?: number; error?: string }
      // 404 = Endpoint (noch) nicht deployt → still ausblenden statt Fehler.
      if (err.status === 404) {
        setUnavailable(true)
      } else {
        showToast('error', `Berechtigungen konnten nicht geladen werden: ${err.error ?? 'Unbekannt'}`)
      }
    } finally {
      setLoading(false)
    }
  }, [userId, showToast])

  useEffect(() => {
    void load()
  }, [load])

  async function addGrant() {
    const trimmed = target.trim()
    if (!trimmed) {
      showToast('error', 'Bitte ein Ziel angeben (z.B. create_* oder einen Tool-Namen).')
      return
    }
    setBusy(true)
    try {
      await api.grantUserPermission(userId, {
        permission_type: permType,
        scope,
        target: trimmed,
        ...(expiresAt ? { expires_at: new Date(expiresAt).toISOString() } : {}),
      })
      setTarget('')
      setExpiresAt('')
      showToast('success', 'Berechtigung hinzugefügt')
      await load()
    } catch (e: unknown) {
      const err = e as { error?: string }
      showToast('error', `Hinzufügen fehlgeschlagen: ${err.error ?? 'Unbekannt'}`)
    } finally {
      setBusy(false)
    }
  }

  async function removeGrant(id: string) {
    setRemovingId(id)
    try {
      await api.revokeUserGrant(userId, id)
      setGrants((prev) => prev.filter((g) => g.id !== id))
      showToast('success', 'Berechtigung entfernt')
    } catch (e: unknown) {
      const err = e as { error?: string }
      showToast('error', `Entfernen fehlgeschlagen: ${err.error ?? 'Unbekannt'}`)
    } finally {
      setRemovingId(null)
    }
  }

  function formatExpiry(s?: string | null): string {
    if (!s) return 'unbefristet'
    return new Date(s).toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' })
  }

  if (unavailable) return null

  return (
    <div style={{ marginTop: '1.5rem', borderTop: '1px solid #e5e7eb', paddingTop: '1rem' }}>
      <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
        Aria Standing-Permissions
      </div>
      <p style={{ margin: '0 0 0.75rem', fontSize: '0.8125rem', color: '#6b7280' }}>
        Regeln für Aria-Tool-Aufrufe dieses Mitglieds. <em>Bestätigung erforderlich</em> löst
        im Chat eine Freigabe-Karte aus, bevor das Tool ausgeführt wird.
      </p>

      {loading ? (
        <p style={{ color: '#6b7280', fontSize: '0.8125rem' }}>Lade …</p>
      ) : grants.length === 0 ? (
        <p style={{ color: '#6b7280', fontSize: '0.8125rem', margin: '0 0 0.75rem' }}>
          Keine Standing-Permissions gesetzt.
        </p>
      ) : (
        <ul style={{ listStyle: 'none', margin: '0 0 0.75rem', padding: 0, display: 'flex', flexDirection: 'column', gap: 6 }}>
          {grants.map((g) => {
            const st = PERMISSION_TYPE_STYLE[g.permission_type]
            return (
              <li
                key={g.id}
                style={{
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'space-between',
                  gap: 8,
                  padding: '0.4rem 0.6rem',
                  border: '1px solid #f3f4f6',
                  borderRadius: 6,
                }}
              >
                <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap', minWidth: 0 }}>
                  <span
                    style={{
                      background: st.bg,
                      color: st.fg,
                      border: `1px solid ${st.border}`,
                      padding: '2px 8px',
                      borderRadius: 999,
                      fontSize: '0.75rem',
                      fontWeight: 600,
                      whiteSpace: 'nowrap',
                    }}
                  >
                    {PERMISSION_TYPE_LABEL[g.permission_type]}
                  </span>
                  <span style={{ fontSize: '0.8125rem', color: '#6b7280' }}>{SCOPE_LABEL[g.scope]}</span>
                  <code style={{ fontSize: '0.8125rem' }}>{g.target}</code>
                  {g.expires_at && (
                    <span style={{ fontSize: '0.7rem', color: '#9ca3af' }}>bis {formatExpiry(g.expires_at)}</span>
                  )}
                </div>
                {canEdit && (
                  <button
                    type="button"
                    onClick={() => removeGrant(g.id)}
                    disabled={removingId === g.id}
                    aria-label="Entfernen"
                    style={{
                      border: 'none',
                      background: 'transparent',
                      color: '#dc2626',
                      cursor: removingId === g.id ? 'default' : 'pointer',
                      fontSize: '0.8125rem',
                      fontWeight: 600,
                      flexShrink: 0,
                    }}
                  >
                    {removingId === g.id ? '…' : 'Entfernen'}
                  </button>
                )}
              </li>
            )
          })}
        </ul>
      )}

      {canEdit && (
        <div
          style={{
            display: 'flex',
            flexWrap: 'wrap',
            gap: 8,
            alignItems: 'flex-end',
            padding: '0.75rem',
            background: '#f9fafb',
            borderRadius: 8,
          }}
        >
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '0.7rem', color: '#6b7280' }}>
            Typ
            <select
              value={permType}
              onChange={(e) => setPermType(e.target.value as CortexPermissionType)}
              style={{ padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.8125rem' }}
            >
              <option value="allow">Erlauben</option>
              <option value="confirm">Bestätigung</option>
              <option value="deny">Verbieten</option>
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '0.7rem', color: '#6b7280' }}>
            Bereich
            <select
              value={scope}
              onChange={(e) => setScope(e.target.value as CortexPermissionScope)}
              style={{ padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.8125rem' }}
            >
              <option value="tool">Tool</option>
              <option value="server">Server</option>
              <option value="category">Kategorie</option>
            </select>
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '0.7rem', color: '#6b7280', flex: 1, minWidth: 120 }}>
            Ziel
            <input
              type="text"
              value={target}
              onChange={(e) => setTarget(e.target.value)}
              placeholder="z.B. create_* oder mail_send"
              style={{ padding: '0.35rem 0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.8125rem' }}
            />
          </label>
          <label style={{ display: 'flex', flexDirection: 'column', gap: 2, fontSize: '0.7rem', color: '#6b7280' }}>
            Ablauf (optional)
            <input
              type="date"
              value={expiresAt}
              onChange={(e) => setExpiresAt(e.target.value)}
              style={{ padding: '0.3rem 0.5rem', border: '1px solid #d1d5db', borderRadius: 6, fontSize: '0.8125rem' }}
            />
          </label>
          <button
            type="button"
            onClick={addGrant}
            disabled={busy}
            style={{
              padding: '0.4rem 0.9rem',
              background: '#3b82f6',
              color: '#fff',
              border: 'none',
              borderRadius: 6,
              fontSize: '0.8125rem',
              fontWeight: 600,
              cursor: busy ? 'default' : 'pointer',
            }}
          >
            {busy ? '…' : 'Hinzufügen'}
          </button>
        </div>
      )}
    </div>
  )
}

export default function MemberDetailDrawer({
  orgSlug,
  memberRef,
  isAdminOrOwner,
  onClose,
  onChanged,
}: Props) {
  const { showToast } = useToast()
  const [tab, setTab] = useState<Tab>('identity')
  const [detail, setDetail] = useState<MemberDetail | null>(null)
  const [groups, setGroups] = useState<RealmGroup[]>([])
  const [loading, setLoading] = useState(true)
  const [savingRole, setSavingRole] = useState(false)

  async function load() {
    setLoading(true)
    try {
      const [d, gs] = await Promise.all([
        api.getMemberDetail(orgSlug, memberRef),
        api.listOrgGroups(orgSlug),
      ])
      setDetail(d)
      setGroups(gs as RealmGroup[])
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      showToast('error', `Detail konnte nicht geladen werden: ${err.response?.data?.error ?? 'Unbekannt'}`)
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [orgSlug, memberRef])

  const memberGroupIds = useMemo(() => {
    return new Set((detail?.groups ?? []).map((g) => g.id))
  }, [detail])

  // Sortierung: Member-Groups zuerst (gecheckt), dann Rest A-Z nach Pfad
  const sortedGroups = useMemo(() => {
    return [...groups].sort((a, b) => {
      const am = memberGroupIds.has(a.id) ? 0 : 1
      const bm = memberGroupIds.has(b.id) ? 0 : 1
      if (am !== bm) return am - bm
      return a.path.localeCompare(b.path, 'de')
    })
  }, [groups, memberGroupIds])

  // toggleGroup wurde entfernt — Berechtigungen-Tab ist jetzt read-only,
  // Verwaltung passiert im Services-Tab je Service-Karte.

  async function changeRole(newRole: 'admin' | 'member' | 'operator' | 'viewer') {
    if (!detail) return
    setSavingRole(true)
    try {
      await api.updateMemberRole(orgSlug, detail.keycloak_id, newRole)
      setDetail({ ...detail, org_role: newRole })
      showToast('success', 'Rolle geändert')
      onChanged()
    } catch (e: unknown) {
      const err = e as { response?: { data?: { error?: string } } }
      showToast('error', `Rolle ändern fehlgeschlagen: ${err.response?.data?.error ?? 'Unbekannt'}`)
    } finally {
      setSavingRole(false)
    }
  }

  function fullName(): string {
    if (!detail) return ''
    if (detail.first_name || detail.last_name) {
      return `${detail.first_name ?? ''} ${detail.last_name ?? ''}`.trim()
    }
    return displayIdentity(detail).split('@')[0]
  }

  function formatDate(s?: string): string {
    if (!s) return '–'
    return new Date(s).toLocaleString('de-DE', {
      day: '2-digit', month: '2-digit', year: 'numeric',
      hour: '2-digit', minute: '2-digit',
    })
  }

  return (
    <div
      role="dialog"
      aria-label={`Mitglied ${fullName()}`}
      style={{
        position: 'fixed',
        top: 0, right: 0, bottom: 0,
        width: 520,
        background: '#fff',
        boxShadow: '-8px 0 24px rgba(0,0,0,0.12)',
        display: 'flex',
        flexDirection: 'column',
        zIndex: 100,
      }}
    >
      {/* Header */}
      <div style={{ padding: '1rem 1.25rem', borderBottom: '1px solid #e5e7eb', display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <div>
          <h2 style={{ margin: 0, fontSize: '1.25rem' }}>{loading ? 'Laden …' : fullName()}</h2>
          <p style={{ margin: '4px 0 0', fontSize: '0.8125rem', color: '#6b7280' }}>
            {detail ? displayIdentity(detail) : null}
          </p>
        </div>
        <button
          type="button"
          onClick={onClose}
          aria-label="Schließen"
          style={{ border: 'none', background: 'transparent', fontSize: '1.5rem', cursor: 'pointer', color: '#6b7280' }}
        >
          ×
        </button>
      </div>

      {/* Tabs */}
      <nav style={{ display: 'flex', borderBottom: '1px solid #e5e7eb' }}>
        {(
          [
            { id: 'identity', label: 'Identität', icon: '👤' },
            { id: 'role', label: 'Org-Rolle', icon: '🎯' },
            { id: 'permissions', label: 'Berechtigungen', icon: '🔑' },
            { id: 'audit', label: 'Audit', icon: '📜' },
          ] as { id: Tab; label: string; icon: string }[]
        ).map((t) => (
          <button
            key={t.id}
            type="button"
            onClick={() => setTab(t.id)}
            style={{
              flex: 1,
              padding: '0.75rem 0.5rem',
              border: 'none',
              borderBottom: tab === t.id ? '2px solid #3b82f6' : '2px solid transparent',
              background: 'transparent',
              cursor: 'pointer',
              color: tab === t.id ? '#3b82f6' : '#6b7280',
              fontWeight: 600,
              fontSize: '0.8125rem',
              display: 'inline-flex',
              alignItems: 'center',
              justifyContent: 'center',
              gap: 6,
            }}
          >
            <span aria-hidden>{t.icon}</span>
            <span>{t.label}</span>
          </button>
        ))}
      </nav>

      {/* Content */}
      <div style={{ flex: 1, overflowY: 'auto', padding: '1rem 1.25rem' }}>
        {loading || !detail ? (
          <p style={{ color: '#6b7280' }}>Lade …</p>
        ) : (
          <>
            {tab === 'identity' && (
              <dl style={{ margin: 0, fontSize: '0.875rem', display: 'grid', gridTemplateColumns: 'auto 1fr', gap: '0.4rem 1rem' }}>
                <dt style={{ color: '#6b7280' }}>Name</dt>           <dd style={{ margin: 0 }}>{fullName()}</dd>
                {!isItsceServiceAccount(detail) && (<><dt style={{ color: '#6b7280' }}>E-Mail</dt>         <dd style={{ margin: 0 }}>{detail.email}</dd></>)}
                <dt style={{ color: '#6b7280' }}>Username</dt>       <dd style={{ margin: 0 }}><code>{isItsceServiceAccount(detail) ? displayIdentity(detail) : detail.username}</code></dd>
                <dt style={{ color: '#6b7280' }}>Account-Type</dt>   <dd style={{ margin: 0 }}>{ACCOUNT_TYPE_LABEL[detail.account_type]}</dd>
                <dt style={{ color: '#6b7280' }}>Status</dt>         <dd style={{ margin: 0 }}>{detail.enabled ? 'Aktiv' : 'Deaktiviert'}</dd>
                <dt style={{ color: '#6b7280' }}>Eingeladen am</dt>  <dd style={{ margin: 0 }}>{formatDate(detail.invited_at)}</dd>
                <dt style={{ color: '#6b7280' }}>Beigetreten am</dt> <dd style={{ margin: 0 }}>{formatDate(detail.accepted_at)}</dd>
                <dt style={{ color: '#6b7280' }}>Keycloak-ID</dt>    <dd style={{ margin: 0 }}><code style={{ fontSize: '0.75rem' }}>{detail.keycloak_id}</code></dd>
              </dl>
            )}

            {tab === 'role' && (
              <div>
                <p style={{ marginTop: 0, color: '#6b7280', fontSize: '0.8125rem' }}>
                  {isTenantInstance()
                    ? 'Rollen werden als Client-Rollen dieses Cockpits (cockpit-owner, cockpit-admin, cockpit-operator, cockpit-viewer) im Keycloak Ihres Unternehmens geführt. Wer keine Rolle hat, hat keinen Zugriff auf das Cockpit. Die Inhaber-Rolle ist geschützt.'
                    : 'Rollen werden in der Plattform-DB gespeichert (`organization_members`), nicht in Keycloak. Inhaber-Rolle ist geschützt — Übergabe folgt in separatem Workflow.'}
                </p>
                {detail.org_role === 'owner' ? (
                  <div style={{ padding: '0.75rem 1rem', background: '#fef3c7', borderRadius: 6, color: '#92400e', fontWeight: 600 }}>
                    🏆 Inhaber — Vollzugriff. Übergabe nur über separaten Workflow.
                  </div>
                ) : isAdminOrOwner ? (
                  <select
                    value={detail.org_role}
                    onChange={(e) => changeRole(e.target.value as 'admin' | 'member' | 'operator' | 'viewer')}
                    disabled={savingRole}
                    style={{
                      padding: '0.5rem 0.75rem',
                      border: '1px solid #d1d5db',
                      borderRadius: 6,
                      fontSize: '0.875rem',
                      width: '100%',
                      maxWidth: 240,
                    }}
                  >
                    {detail.org_role === 'none' && <option value="none" disabled>Keine Rolle</option>}
                    <option value="admin">Administrator</option>
                    {isTenantInstance() ? (
                      <>
                        <option value="operator">Operator</option>
                        <option value="viewer">Betrachter</option>
                      </>
                    ) : (
                      <option value="member">Mitglied</option>
                    )}
                  </select>
                ) : (
                  <div style={{ padding: '0.5rem 0.75rem', background: '#f3f4f6', borderRadius: 6 }}>
                    {detail.org_role === 'admin' ? 'Administrator' : detail.org_role === 'operator' ? 'Operator' : detail.org_role === 'viewer' ? 'Betrachter' : detail.org_role === 'none' ? 'Keine Rolle' : 'Mitglied'}
                  </div>
                )}
              </div>
            )}

            {tab === 'permissions' && (
              <div>
                <div style={{ padding: '0.625rem 0.875rem', background: '#dbeafe', borderRadius: 6, fontSize: '0.8125rem', color: '#1e40af', marginBottom: '0.75rem' }}>
                  Uebersicht der Zugriffe dieses Nutzers. Verwaltung pro Service
                  passiert im Tab <strong>Services</strong> &rarr; jeweilige Service-Karte &rarr;
                  Sub-Tab <em>Zugriff</em>.
                </div>
                {(() => {
                  // Trenne Service-Groups (/services/<name>) von Sonstigen
                  // (Teams, Legacy). Service-Groups bekommen schoene Badges,
                  // alles andere als kleine Liste darunter.
                  const memberGroups = sortedGroups.filter((g) => memberGroupIds.has(g.id))
                  const serviceGroups = memberGroups.filter((g) => g.path.startsWith('/services/'))
                  const otherGroups = memberGroups.filter((g) => !g.path.startsWith('/services/'))
                  if (memberGroups.length === 0) {
                    return <p style={{ color: '#6b7280' }}>Keine Berechtigungen — dieser Nutzer hat noch keinen Service-Zugriff.</p>
                  }
                  return (
                    <>
                      {serviceGroups.length > 0 && (
                        <>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                            Service-Zugriff
                          </div>
                          <div style={{ display: 'flex', flexWrap: 'wrap', gap: 8, marginBottom: 16 }}>
                            {serviceGroups.map((g) => (
                              <span
                                key={g.id}
                                style={{
                                  background: '#ecfdf5',
                                  color: '#065f46',
                                  border: '1px solid #a7f3d0',
                                  padding: '4px 12px',
                                  borderRadius: 999,
                                  fontSize: '0.8125rem',
                                  fontWeight: 600,
                                }}
                                title={g.path}
                              >
                                {g.name}
                              </span>
                            ))}
                          </div>
                        </>
                      )}
                      {otherGroups.length > 0 && (
                        <>
                          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#6b7280', textTransform: 'uppercase', letterSpacing: '0.05em', marginBottom: 8 }}>
                            Weitere Gruppen
                          </div>
                          <ul style={{ listStyle: 'none', margin: 0, padding: 0 }}>
                            {otherGroups.map((g) => (
                              <li
                                key={g.id}
                                style={{ padding: '0.375rem 0', borderBottom: '1px solid #f3f4f6', fontSize: '0.8125rem' }}
                              >
                                <span style={{ fontWeight: 600 }}>{g.name}</span>
                                <code style={{ marginLeft: 8, fontSize: '0.75rem', color: '#9ca3af' }}>{g.path}</code>
                              </li>
                            ))}
                          </ul>
                        </>
                      )}
                    </>
                  )
                })()}

                {/* Kunden-Instanz: Cortex-Dauerfreigaben laufen zentral, cockpit-api hat sie nicht (TQ #409). */}
                {!isTenantInstance() && <StandingPermissionsEditor userId={detail.keycloak_id} canEdit={isAdminOrOwner} />}
              </div>
            )}

            {tab === 'audit' && (
              <div>
                <p style={{ color: '#6b7280', fontSize: '0.875rem' }}>
                  Audit-Log kommt mit Aria-Integration im Cockpit. Geplant:
                  Login-Events aus Keycloak + Aria-Aktionen aus Cortex-Audit-Log.
                </p>
                <div style={{ padding: '1rem', background: '#f9fafb', borderRadius: 6, color: '#9ca3af', fontStyle: 'italic', textAlign: 'center' }}>
                  Demnächst verfügbar
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  )
}
